import { useState, useEffect, useCallback } from 'react';
import dataPreloadService from '../services/dataPreloadService';

export const useDataPreload = (options = {}) => {
  const [isPreloading, setIsPreloading] = useState(false);
  const [preloadError, setPreloadError] = useState(null);
  const [preloadProgress, setPreloadProgress] = useState(0);
  const [lastPreloadTime, setLastPreloadTime] = useState(null);
  const [cacheStatus, setCacheStatus] = useState(null);

  const {
    autoPreload = true,
    showProgress = false,
    retryOnError = true
  } = options;

  // Update cache status
  const updateCacheStatus = useCallback(() => {
    try {
      const status = dataPreloadService.getCacheStatus();
      setCacheStatus(status);
    } catch (error) {
      console.error('[useDataPreload] Failed to get cache status:', error);
    }
  }, []);

  // Start preloading
  const startPreload = useCallback(async (forceRefresh = false) => {
    if (isPreloading) {
      return dataPreloadService.preloadPromise;
    }

    setIsPreloading(true);
    setPreloadError(null);
    setPreloadProgress(0);

    try {
      
      // If force refresh, clear cache first
      if (forceRefresh) {
        await dataPreloadService.forceRefreshAll();
      }

      // Start preloading with progress tracking
      const startTime = Date.now();
      
      // Simulate progress updates (since we can't track real progress with Promise.all)
      if (showProgress) {
        const progressInterval = setInterval(() => {
          setPreloadProgress(prev => {
            const elapsed = Date.now() - startTime;
            // Estimate progress based on elapsed time (assuming max 10 seconds)
            const estimated = Math.min(90, (elapsed / 10000) * 100);
            return estimated;
          });
        }, 100);

        // Clear interval when preloading completes
        dataPreloadService.onPreloadComplete(() => {
          clearInterval(progressInterval);
          setPreloadProgress(100);
        });
      }

      const result = await dataPreloadService.preloadAllData();
      
      setLastPreloadTime(new Date());
      setPreloadProgress(100);
      
      
      // Update cache status after successful preload
      updateCacheStatus();
      
      return result;
    } catch (error) {
      setPreloadError(error);
      
      // Retry logic
      if (retryOnError) {
        setTimeout(() => {
          startPreload(forceRefresh);
        }, 3000); // Wait 3 seconds before retrying
      }
      
      throw error;
    } finally {
      setIsPreloading(false);
    }
  }, [isPreloading, showProgress, retryOnError, updateCacheStatus]);

  // Auto preload on mount if needed
  useEffect(() => {
    if (autoPreload) {
      const status = dataPreloadService.getPreloadStatus();
      
      if (status.needsPreload) {
        startPreload();
      } else {
        setLastPreloadTime(status.lastPreloadTime);
        updateCacheStatus();
      }
    }
  }, [autoPreload, startPreload, updateCacheStatus]);

  // Listen for online events to trigger preload
  useEffect(() => {
    const handleOnline = () => {
      const status = dataPreloadService.getPreloadStatus();
      if (status.needsPreload && !isPreloading) {
        startPreload();
      }
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [isPreloading, startPreload]);

  // Refresh data
  const refreshData = useCallback(async () => {
    return startPreload(true);
  }, [startPreload]);

  // Get preload status
  const getPreloadStatus = useCallback(() => {
    return dataPreloadService.getPreloadStatus();
  }, []);

  return {
    isPreloading,
    preloadError,
    preloadProgress,
    lastPreloadTime,
    cacheStatus,
    startPreload,
    refreshData,
    getPreloadStatus,
    updateCacheStatus
  };
};

export default useDataPreload;